import { ComponentProps, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomDrawer from "../components/CustomDrawer";
import ReportingDocumentSection from "../components/Reporting/ReportingDocumentSection";
import { hasFolderParam } from "../utils/sharepointLinks";

type ReportDocumentsType = ComponentProps<
	typeof ReportingDocumentSection
>["type"];

// A report's Additional documents, in a drawer that open() opens from a button
// on the report's page. A folder deep link, such as a file preview's
// breadcrumb, opens the page with the drawer open on that folder.
function useReportDocumentsDrawer(
	type: ReportDocumentsType,
	reportId: string,
	record: any
) {
	const location = useLocation();
	const [isOpen, setIsOpen] = useState(() => hasFolderParam(location.search));
	// Browser Back or Forward can show another report without remounting the
	// page; it starts with the drawer closed.
	const [openReportId, setOpenReportId] = useState(reportId);
	if (openReportId !== reportId) {
		setOpenReportId(reportId);
		setIsOpen(false);
	}

	// Closing it unmounts the section, so each opening starts from Contents.
	// Not open before the report has loaded: one that doesn't load has no
	// documents to show. The key keeps it the same drawer when a page renders
	// it in another place, such as its error view after a failed refetch.
	const drawer = (
		<CustomDrawer
			key="report-documents-drawer"
			title="Additional documents"
			open={isOpen && !!record}
			close={() => setIsOpen(false)}
		>
			{record && <ReportingDocumentSection type={type} record={record} />}
		</CustomDrawer>
	);

	return { open: () => setIsOpen(true), drawer };
}

export default useReportDocumentsDrawer;
